import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type LeftNavLayoutProps = {
  /** Navigation sidebar content */
  nav: ReactNode;
  /** Main content area */
  children: ReactNode;
  /** Whether sidebar is collapsed (icon-only mode) */
  collapsed?: boolean;
  /** Custom className for the layout container */
  className?: string;
  /** Custom className for the nav sidebar */
  navClassName?: string;
  /** Custom className for the content area */
  contentClassName?: string;
  /** Width of the sidebar when expanded */
  navWidth?: string;
  /** Width of the sidebar when collapsed */
  navWidthCollapsed?: string;
  /** Hide navigation on mobile (slides in as overlay when toggled) */
  hideOnMobile?: boolean;
  /** Auto-collapse to icon-only mode on mobile screens */
  autoCollapseOnMobile?: boolean;
  /** Embedded demo mode (no fixed positioning, constrained height) */
  embedded?: boolean;
  /** Ref for the main content scrollable element */
  mainContentRef?: React.RefObject<HTMLDivElement>;
  /** Optional header text for the navigation sidebar */
  header?: string;
  /** Optional top navbar content rendered above children in the right column */
  topNav?: ReactNode;
  /** Href for the logo link in the sidebar header */
  homeHref?: string;
  /** Optional footer rendered at the bottom of the sidebar, receives the effective collapsed state */
  footer?: (collapsed: boolean) => ReactNode;
  /** Callback to toggle the sidebar collapsed state - renders a collapse button in the sidebar header */
  onToggleCollapsed?: () => void;
};

/**
 * LeftNavLayout - Full-featured responsive left navigation layout
 *
 * Provides a robust sidebar navigation with:
 * - Full and collapsed (icon-only) states
 * - Smooth transitions without janky spacing
 * - Proper scroll handling
 * - Responsive mobile behavior
 * - Accessible keyboard navigation
 *
 * @example
 * ```tsx
 * <LeftNavLayout
 *   collapsed={isCollapsed}
 *   onCollapsedChange={setIsCollapsed}
 *   nav={
 *     <>
 *       <LeftNavSection>
 *         <LeftNavItem icon={<HomeIcon />} href="/" active>Home</LeftNavItem>
 *         <LeftNavItem icon={<DashboardIcon />} href="/dashboard">Dashboard</LeftNavItem>
 *       </LeftNavSection>
 *     </>
 *   }
 * >
 *   <main>Your content here</main>
 * </LeftNavLayout>
 * ```
 */
function LeftNavLayout({
  nav,
  children,
  collapsed: controlledCollapsed,
  className = '',
  navClassName = '',
  contentClassName = '',
  navWidth = '16rem',
  navWidthCollapsed = '4.5rem',
  hideOnMobile = false,
  autoCollapseOnMobile = true,
  embedded = false,
  mainContentRef,
  topNav,
  homeHref = '/',
  footer,
  onToggleCollapsed,
}: Readonly<LeftNavLayoutProps>) {
  const STORAGE_KEY_DESKTOP = 'left-nav-desktop-collapsed';
  const STORAGE_KEY_MOBILE = 'left-nav-mobile-collapsed';

  // Initialize desktop collapsed state from localStorage (defaults to false = expanded)
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const saved = localStorage.getItem(STORAGE_KEY_DESKTOP);
      return saved !== null ? saved === 'true' : false;
    } catch (error) {
      console.warn(
        'Failed to read desktop collapsed state from localStorage:',
        error
      );
      return false;
    }
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(!hideOnMobile);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);

  // Initialize mobile collapsed state from localStorage (defaults to true = collapsed)
  const [mobileCollapsedOverride, setMobileCollapsedOverride] = useState<
    boolean | null
  >(() => {
    if (typeof window === 'undefined') return null;
    try {
      const saved = localStorage.getItem(STORAGE_KEY_MOBILE);
      // If user has never toggled, return null to use default (collapsed)
      // If they have toggled, use their saved preference
      return saved !== null ? saved === 'true' : null;
    } catch (error) {
      console.warn(
        'Failed to read mobile collapsed state from localStorage:',
        error
      );
      return null;
    }
  });

  const desktopCollapsedRef = useRef<boolean | undefined>(undefined);

  const navRef = useRef<HTMLDivElement | null>(null);
  const scrollPosRef = useRef<number>(0);
  const internalContentRef = useRef<HTMLElement | null>(null);
  const contentRef = mainContentRef ?? internalContentRef;

  // Detect mobile screen size
  // Use useLayoutEffect to run synchronously before paint, preventing layout shift
  useLayoutEffect(() => {
    const checkMobile = () => {
      const newIsMobile = window.innerWidth < 1024; // lg breakpoint
      if (newIsMobile !== isMobile) {
        const wasDesktop = !isMobile;
        const isNowDesktop = !newIsMobile;

        setIsMobile(newIsMobile);

        // When switching from desktop to mobile, save the desktop state
        if (wasDesktop && autoCollapseOnMobile) {
          const currentCollapsed = controlledCollapsed ?? internalCollapsed;
          desktopCollapsedRef.current = currentCollapsed;
        }

        // When switching from mobile to desktop, restore the desktop state
        if (
          isNowDesktop &&
          autoCollapseOnMobile &&
          desktopCollapsedRef.current !== undefined
        ) {
          if (controlledCollapsed === undefined) {
            // Only restore if using uncontrolled state
            setInternalCollapsed(desktopCollapsedRef.current);
          }
        }

        // When switching to mobile, restore mobile state from localStorage
        if (!isNowDesktop && autoCollapseOnMobile) {
          try {
            const savedMobile = localStorage.getItem(STORAGE_KEY_MOBILE);
            setMobileCollapsedOverride(
              savedMobile !== null ? savedMobile === 'true' : null
            );
          } catch {
            setMobileCollapsedOverride(null);
          }
        } else if (isNowDesktop) {
          // When switching to desktop, clear mobile override (not needed on desktop)
          setMobileCollapsedOverride(null);
        }
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile, autoCollapseOnMobile, controlledCollapsed, internalCollapsed]);

  // Use controlled or uncontrolled collapsed state
  const collapsed = controlledCollapsed ?? internalCollapsed;

  // Determine effective collapsed state.
  // If controlled from outside, always honour the prop.
  // If uncontrolled, apply the mobile-override logic.
  const effectiveCollapsed =
    controlledCollapsed !== undefined
      ? controlledCollapsed
      : autoCollapseOnMobile && isMobile
        ? (mobileCollapsedOverride ?? true)
        : collapsed;

  useEffect(() => {
    const root = document.documentElement;
    const desktopNavWidth = effectiveCollapsed ? navWidthCollapsed : navWidth;
    const shouldApplyDesktopOffset = !isMobile;

    root.style.setProperty(
      '--app-shell-left-nav-width',
      shouldApplyDesktopOffset ? desktopNavWidth : '0px'
    );

    return () => {
      root.style.setProperty('--app-shell-left-nav-width', '0px');
    };
  }, [effectiveCollapsed, isMobile, navWidth, navWidthCollapsed]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Delay data-collapsed by the transition duration so justify-center on nav items
  // only fires after text has already faded, preventing the icon-jump on collapse.
  // On expand, restore immediately so text fades in as the sidebar widens.
  const [navDataCollapsed, setNavDataCollapsed] = useState(effectiveCollapsed);
  const collapsedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    clearTimeout(collapsedTimerRef.current);
    if (effectiveCollapsed) {
      collapsedTimerRef.current = setTimeout(
        () => setNavDataCollapsed(true),
        200
      );
    } else {
      setNavDataCollapsed(false);
    }
    return () => clearTimeout(collapsedTimerRef.current);
  }, [effectiveCollapsed]);

  // Restore nav scroll position after children change (route switch) & reset scroll
  useEffect(() => {
    if (navRef.current) {
      navRef.current.scrollTop = scrollPosRef.current;
    }
  }, [children]);

  const containerClasses = embedded
    ? 'flex bg-background border border-border rounded-lg overflow-hidden'
    : 'flex h-screen bg-background';

  return (
    <div className={`${containerClasses} ${className}`}>
      {/* Mobile Backdrop Overlay - Only shows on mobile when menu is open */}
      {!embedded && hideOnMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 transition-opacity duration-300 lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Sidebar Navigation */}
      <aside
        className={`${embedded ? 'relative flex h-full flex-col' : hideOnMobile ? 'fixed top-11 left-0 z-40 h-[calc(100vh-2.75rem)] md:top-11 lg:relative lg:top-0 lg:z-10 lg:h-full' : 'relative h-full'} bg-sidebar border-sidebar-border flex shrink-0 flex-col overflow-visible border-r transition-[width,transform] duration-200 ease-out ${!embedded && hideOnMobile && !mobileMenuOpen ? '-translate-x-full lg:translate-x-0' : 'translate-x-0'} ${effectiveCollapsed ? 'w-18' : 'w-64'} ${navClassName}`}
        style={
          (!effectiveCollapsed && navWidth !== '16rem') ||
          (effectiveCollapsed && navWidthCollapsed !== '4.5rem')
            ? ({
                width: effectiveCollapsed ? navWidthCollapsed : navWidth,
              } as React.CSSProperties)
            : undefined
        }
        aria-label="Main navigation"
      >
        {/* Nav Header with Logo + Toggle */}
        <div className="border-sidebar-border relative flex h-14 shrink-0 items-center gap-2 overflow-visible border-b px-2">
          <Link
            to={homeHref}
            className={cn(
              'flex items-center gap-2.5 rounded-md px-1 py-1',
              effectiveCollapsed ? 'justify-center' : 'flex-1'
            )}
          >
            <img
              alt="Hydden"
              src="/assets/logos/hydden.svg"
              className="shrink-0 object-contain"
              style={{ width: 32, height: 32 }}
            />
            <span
              className={cn(
                'text-sidebar-accent-foreground overflow-hidden whitespace-nowrap text-[22px] font-bold tracking-tight transition-[opacity,max-width] duration-200',
                effectiveCollapsed
                  ? 'max-w-0 opacity-0'
                  : 'max-w-40 opacity-100'
              )}
            >
              HYDDEN
            </span>
          </Link>
          {onToggleCollapsed && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={onToggleCollapsed}
                    className="bg-background text-sidebar-foreground absolute -right-6 z-10 flex size-8 shrink-0 items-center justify-center rounded-full shadow-md transition-colors hover:cursor-pointer hover:shadow-lg"
                  >
                    {effectiveCollapsed ? (
                      <PanelLeftOpen className="size-4" />
                    ) : (
                      <PanelLeftClose className="size-4" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Nav Content - Scrollable */}
        <nav
          ref={navRef}
          className="flex-1 overflow-x-hidden overflow-y-auto"
          data-collapsed={navDataCollapsed}
          data-pre-collapsed={effectiveCollapsed}
          onScroll={(e) => {
            scrollPosRef.current = (
              e.currentTarget as HTMLDivElement
            ).scrollTop;
          }}
        >
          <div className="pb-1">{nav}</div>
        </nav>
        {footer && (
          <div className="border-sidebar-border shrink-0 border-t p-2">
            {footer(effectiveCollapsed)}
          </div>
        )}
      </aside>

      {/* Right Column: Top Nav + Main Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {topNav && <div className="shrink-0">{topNav}</div>}
        <main
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`flex-1 overflow-y-auto ${embedded ? 'h-60' : ''} ${contentClassName} scrollbar-thin`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

LeftNavLayout.displayName = 'LeftNavLayout';

export default LeftNavLayout;
